import { ScrollReveal, SplitReveal } from '@/components/motion';
import { ADVISORY_BOARD, MANAGEMENT_TEAM } from '@/data/about';

/** Typographic initials for a monogram avatar — honorifics ("Dr.") stripped. */
function initialsOf(name: string): string {
  return name
    .replace(/\b(dr|prof|mr|mrs|ms)\.?/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * LeadershipBoard (/about — section 2) — "Management & international advisory
 * board." Renders the full MANAGEMENT_TEAM as plain hover-lift cards, each with
 * a typographic monogram avatar (no stock faces), the person's name, title, and
 * bio where the source publishes one. A distinct sub-block below lists the five
 * ADVISORY_BOARD names as monogram cards labelled "Advisory Board Member" —
 * the source publishes no titles or bios for them, so none are invented.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger across both grids.
 * All primitives are reduced-motion safe and never strand content hidden.
 * Every name, title and bio is read verbatim from '@/data/about'.
 */
export function LeadershipBoard() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Leadership
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Management &amp; international advisory board.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              The people leading IBAS day to day, and the international advisory board
              that supports the institution&rsquo;s direction.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Management team — plain hover-lift cards ─────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {MANAGEMENT_TEAM.map((leader) => (
            <div
              key={leader.name}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <span
                aria-hidden
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy font-display text-sm font-semibold tracking-wide text-brand-gold-light"
              >
                {initialsOf(leader.name)}
              </span>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {leader.name}
              </h3>
              <p className="mt-1 text-sm font-medium text-brand-navy">{leader.title}</p>
              {leader.bio ? (
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{leader.bio}</p>
              ) : null}
            </div>
          ))}
        </ScrollReveal>

        {/* ── International Advisory Board — names only, no titles/bios ─── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            International Advisory Board
          </p>
          <ScrollReveal
            stagger={0.08}
            className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {ADVISORY_BOARD.map((name) => (
              <div
                key={name}
                className="flex h-full items-center gap-4 rounded-2xl border border-border bg-background p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy font-display text-sm font-semibold tracking-wide text-brand-gold-light"
                >
                  {initialsOf(name)}
                </span>
                <div className="min-w-0">
                  <p className="font-display text-base leading-tight font-semibold text-foreground">
                    {name}
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">Advisory Board Member</p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
